/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../services/api";
import { queryKeys } from "./queries";

import URL from "../../config/envURLs";
import axiosInstance from "../../config/axiosConfig";

import CONSTANTS from "../../constants/constants";

const { HTTP_MULTIPART_FORM_HEADERS } = CONSTANTS;

// Mutations
export const useLoadToteToTrolley = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      toteId,
      trolleyId,
      toteRecordId,
    }: {
      toteId: string;
      trolleyId: string;
      toteRecordId?: number;
    }) => api.loadToteToTrolley(toteRecordId ?? toteId, trolleyId),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries to refetch updated data
      queryClient.invalidateQueries({
        queryKey: queryKeys.tote(variables.toteId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.trolley(variables.trolleyId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.totes() });
      queryClient.invalidateQueries({ queryKey: queryKeys.trolleys() });
    },
  });
};

export const useUnloadToteFromTrolley = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      toteId,
      trolleyId,
      toteRecordId,
    }: {
      toteId: string;
      trolleyId?: string;
      toteRecordId?: number;
    }) => api.unloadToteFromTrolley(toteRecordId ?? toteId, trolleyId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tote(variables.toteId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.totes() });
      if (variables.trolleyId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.trolley(variables.trolleyId),
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.trolleys() });
    },
  });
};

export const useUpdateTotePriority = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recordId,
      priorityId,
    }: {
      toteId: string;
      recordId: number;
      priorityId: number;
      trolleyId?: string;
    }) => api.markTotePriority(recordId, priorityId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tote(variables.toteId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.totes() });
      if (variables.trolleyId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.trolley(variables.trolleyId),
        });
      }
    },
  });
};

export const useMarkTrolleyServiceable = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      trolleyId,
      isServiceable,
    }: {
      trolleyId: string | number;
      isServiceable: boolean;
      trolleyKey?: string;
    }) => api.markTrolleyServiceable(trolleyId, isServiceable),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.trolley(
          variables.trolleyKey ?? String(variables.trolleyId)
        ),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.trolleys() });
    },
  });
};

export const useUpdateToteLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      toteId,
      locationBarcode,
      locationType,
      toteRecordId,
    }: {
      toteId: string;
      locationBarcode: string;
      locationType: 'In' | 'Out';
      toteRecordId?: number;
    }) => api.updateToteLocation(toteRecordId ?? toteId, locationBarcode, locationType),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tote(variables.toteId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.totes() });
    },
  });
};

export const useUpdateTrolleyLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      trolleyId,
      locationBarcode,
      locationType,
    }: {
      trolleyId: string;
      locationBarcode: string;
      locationType: 'In' | 'Out';
    }) => api.updateTrolleyLocation(trolleyId, locationBarcode, locationType),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.trolley(variables.trolleyId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.trolleys() });
      queryClient.invalidateQueries({ queryKey: queryKeys.totes() });
    },
  });
};

export const useRegisterTrolley = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { 
      trolleyId: string;
      mdu: string; // MDU is now required
      statusId?: number;
    }) => api.addTrolley({ 
      trolley_id: payload.trolleyId,
      mdu: payload.mdu, // MDU is required
      status_id: payload.statusId,
    }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trolley(variables.trolleyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.trolleys() });
    },
  });
};

// New Tote Management Mutations
export const useAddTote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      tote_id: string;
      msn: string;
      usn: string;
      item_count: string;
      weight: string;
      status?: number;
      point_of_delivery?: string;
      date_of_delivery?: string;
      point_of_use_station?: string;
      date_of_use?: string;
    }) => api.addTote(payload),
    onSuccess: (_, variables) => {
      // Invalidate tote list queries
      queryClient.invalidateQueries({ queryKey: queryKeys.totes() });
      // Invalidate specific tote query so it refetches when navigating to tote info
      queryClient.invalidateQueries({ queryKey: queryKeys.tote(variables.tote_id) });
    },
  });
};

export const useCheckTote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { tote_id: string }) =>
      api.checkTote(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tote(variables.tote_id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.totes() });
    },
  });
};

export const login = async (payload: any) => {
  return axiosInstance
    .post(`${URL.API.baseURL}authentication/login`, payload, {
      headers: HTTP_MULTIPART_FORM_HEADERS,
    })
    .then((res) => res.data);
};
